import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
} from '@theme/utilities';
import { Scroller, scrollIntoView, MONOLYT_GALLERY_DURATION } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const ZOOM_DIALOG_CLOSE_TRANSITION_TIMEOUT_MS = 2000;

/**
 * Resolve one explicit Previous/Next step while keeping fullscreen navigation available at both ends.
 *
 * @param {number} currentIndex
 * @param {number} mediaCount
 * @param {-1 | 1} step
 * @returns {number}
 */
export function getAdjacentMediaIndex(currentIndex, mediaCount, step) {
  if (
    !Number.isInteger(currentIndex) ||
    !Number.isInteger(mediaCount) ||
    mediaCount <= 1 ||
    currentIndex < 0 ||
    currentIndex >= mediaCount ||
    (step !== -1 && step !== 1)
  ) {
    return -1;
  }

  return (currentIndex + step + mediaCount) % mediaCount;
}

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} mediaList - The scrolling media list.
 * @property {HTMLElement} [zoomControls] - PDP-only zoom controls.
 * @property {HTMLButtonElement} [zoomOutButton] - PDP-only Zoom Out button.
 * @property {HTMLButtonElement} [zoomResetButton] - PDP-only reset button.
 * @property {HTMLButtonElement} [zoomInButton] - PDP-only Zoom In button.
 * @property {HTMLElement} [zoomValue] - PDP-only live percentage.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #highResImagesLoading = /** @type {Set<string>} */ (new Set());
  #activeDetailZoomIndex = 0;
  #scrollTarget = /** @type {HTMLElement | null} */ (null);
  #mobileLayoutQuery = /** @type {MediaQueryList | null} */ (null);
  #layoutFrame = 0;
  #galleryScroll = null;
  #galleryGesture = null;
  #gallerySettling = false;
  #galleryRevision = 0;

  get #detailZoomEnabled() {
    return this.hasAttribute('data-detail-zoom');
  }

  get #activeDetailZoom() {
    if (!this.#detailZoomEnabled) return null;
    return this.refs.media[this.#activeDetailZoomIndex]?.querySelector('product-detail-zoom') ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#scrollTarget = this.#detailZoomEnabled ? this.refs.mediaList : this.refs.dialog;
    this.#scrollTarget?.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('cancel', this.handleCancel);
    this.#mobileLayoutQuery = window.matchMedia('(max-width: 749px)');
    this.#mobileLayoutQuery.addEventListener('change', this.#handleLayoutChange);
    if (this.#detailZoomEnabled) {
      this.#galleryScroll = new Scroller(this.refs.mediaList, { onScroll: () => this.handleScroll(), smoothDuration: MONOLYT_GALLERY_DURATION });
      this.addEventListener('product-detail-zoom:gallery', this.#handleGalleryGesture);
      this.addEventListener('product-detail-zoom:change', this.#handleDetailZoomChange);
      this.#syncZoomControls();
    }
  }

  disconnectedCallback() {
    this.#stopGallery();
    this.#galleryScroll?.destroy();
    this.#galleryScroll = null;
    this.removeEventListener('product-detail-zoom:gallery', this.#handleGalleryGesture);
    super.disconnectedCallback();
    this.#scrollTarget?.removeEventListener('scroll', this.handleScroll);
    this.#scrollTarget = null;
    this.refs.dialog.removeEventListener('cancel', this.handleCancel);
    this.#mobileLayoutQuery?.removeEventListener('change', this.#handleLayoutChange);
    this.#mobileLayoutQuery = null;
    cancelAnimationFrame(this.#layoutFrame);
    this.#layoutFrame = 0;
    this.removeEventListener('product-detail-zoom:change', this.#handleDetailZoomChange);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#setActiveDetailZoom(index, true);
    if (targetImage) this.loadHighResolutionImage(targetImage);

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      this.#syncZoomControls();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      await this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl) ||
      this.#highResImagesLoading.has(highResolutionUrl)
    ) {
      return false;
    }

    this.#highResImagesLoading.add(highResolutionUrl);

    const highResolutionImage = new Image();
    highResolutionImage.decoding = 'async';

    // Preserve the active gesture target and every semantic/layout attribute. Only
    // promote its responsive source after the maximum-size asset has loaded.
    highResolutionImage.onload = () => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = highResolutionUrl;
      this.#highResImagesLoaded.add(highResolutionUrl);
      this.#highResImagesLoading.delete(highResolutionUrl);
    };

    highResolutionImage.onerror = () => {
      this.#highResImagesLoading.delete(highResolutionUrl);
    };

    highResolutionImage.src = highResolutionUrl;
    return true;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    if (this.#galleryGesture || this.#gallerySettling || !this.refs.dialog.open) return;
    const revision = this.#galleryRevision;
    const mostVisibleElement = await getMostVisibleElement(media);
    if (revision !== this.#galleryRevision || this.#galleryGesture || this.#gallerySettling || !this.refs.dialog.open) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (activeIndex < 0) return;

    this.#setActiveDetailZoom(activeIndex);

    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });
    }

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    this.#stopGallery();
    const { dialog, media } = this.refs;
    this.#resetDetailZooms();

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    let closeFallbackTimer;
    let closeLifecycleFinished = false;
    let dialogClosed = false;
    const closeDialogOnce = () => {
      if (dialogClosed) return;
      dialogClosed = true;
      this.closeDialog();
    };

    try {
      const transitionFinished = (async () => {
        dialog.classList.add('dialog--closed');
        await onAnimationEnd(this.refs.thumbnails);
        if (closeLifecycleFinished) return;

        mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);
        await startViewTransition(() => {
          if (closeLifecycleFinished) return;
          mostVisibleElement.style.removeProperty('view-transition-name');
          slide.style.setProperty('view-transition-name', itemTransitionName);
          closeDialogOnce();
        });
      })();
      const transitionTimeout = new Promise((_, reject) => {
        closeFallbackTimer = window.setTimeout(() => {
          const error = new Error('Zoom dialog close View Transition timed out');
          error.name = 'TimeoutError';
          reject(error);
        }, ZOOM_DIALOG_CLOSE_TRANSITION_TIMEOUT_MS);
      });

      await Promise.race([transitionFinished, transitionTimeout]);
    } catch {
      closeDialogOnce();
    } finally {
      closeLifecycleFinished = true;
      if (closeFallbackTimer !== undefined) window.clearTimeout(closeFallbackTimer);
      closeDialogOnce();
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.removeProperty('view-transition-name');
      dialog.classList.remove('dialog--closed');
      document.documentElement.style.removeProperty('--gallery-media-focal-point');
    }
  }

  closeDialog() {
    this.#stopGallery();
    const { dialog } = this.refs;
    this.#resetDetailZooms();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /** Route native Escape/System Back through the same reset and pause lifecycle. */
  handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** Increase the active PDP image by one explicit zoom step. */
  zoomIn() {
    const wrapper = this.#activeDetailZoom;
    if (!wrapper || typeof wrapper.zoomIn !== 'function') return;

    const media = this.refs.media[this.#activeDetailZoomIndex];
    if (media) this.loadHighResolutionImage(media);
    wrapper.zoomIn();
  }

  /** Decrease the active PDP image by one explicit zoom step. */
  zoomOut() {
    const wrapper = this.#activeDetailZoom;
    if (!wrapper || typeof wrapper.zoomOut !== 'function') return;
    wrapper.zoomOut();
  }

  /** Restore the active PDP image to its complete fit-to-screen view. */
  resetZoom() {
    const wrapper = this.#activeDetailZoom;
    if (!wrapper || typeof wrapper.resetZoom !== 'function') return;
    wrapper.resetZoom();
  }

  /** Select the preceding fullscreen medium, wrapping from first to last. */
  previousMedia() {
    return this.#selectAdjacentMedia(-1);
  }

  /** Select the following fullscreen medium, wrapping from last to first. */
  nextMedia() {
    return this.#selectAdjacentMedia(1);
  }

  /** @param {-1 | 1} step */
  #selectAdjacentMedia(step) {
    const index = getAdjacentMediaIndex(this.#activeDetailZoomIndex, this.refs.media.length, step);
    if (index < 0) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    return this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.media.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    const revision = ++this.#galleryRevision;
    this.#galleryGesture = null;
    this.#gallerySettling = !!this.#galleryScroll;

    this.#setActiveDetailZoom(index);

    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });

      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = media[index];

    if (targetImage) {
      if (this.#galleryScroll) {
        this.#galleryScroll.to(this.#galleryPosition(targetImage), { instant: options.behavior === 'instant' || prefersReducedMotion() });
        this.#galleryScroll.finished.then(() => {
          if (revision !== this.#galleryRevision) return;
          this.#gallerySettling = false;
          if (this.#galleryScroll) this.#galleryScroll.snap = true;
          this.handleScroll();
        });
      } else {
        targetImage.scrollIntoView({ behavior: options.behavior });
      }

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Select the image wrapper that owns all PDP-only control and keyboard actions.
   *
   * @param {number} index
   * @param {boolean} [resetAll]
   */
  #setActiveDetailZoom(index, resetAll = false) {
    if (!this.#detailZoomEnabled) return;

    const previous = this.#activeDetailZoom;
    const activeElement = document.activeElement;
    const previousHadFocus =
      previous instanceof HTMLElement &&
      activeElement instanceof Element &&
      (previous === activeElement || previous.contains(activeElement));
    const activeIndexChanged = index !== this.#activeDetailZoomIndex;
    this.#activeDetailZoomIndex = index;

    if (resetAll) {
      this.#resetDetailZooms();
    } else if (activeIndexChanged) {
      if (previous && typeof previous.resetZoom === 'function') previous.resetZoom();
    }

    this.querySelectorAll('product-detail-zoom').forEach((wrapper) => {
      wrapper.tabIndex = wrapper === this.#activeDetailZoom ? 0 : -1;
    });

    if (previousHadFocus && activeIndexChanged) {
      const nextFocusTarget =
        this.#activeDetailZoom ??
        this.refs.thumbnails.children[index] ??
        this.refs.dialog.querySelector('.dialog-zoomed-gallery__close-button');
      if (nextFocusTarget instanceof HTMLElement) nextFocusTarget.focus({ preventScroll: true });
    }
    this.#syncZoomControls();
  }

  #handleLayoutChange = () => {
    if (!this.#detailZoomEnabled || !this.refs.dialog.open) return;

    this.#stopGallery();
    const activeIndex = this.#activeDetailZoomIndex;
    cancelAnimationFrame(this.#layoutFrame);
    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = 0;
      if (this.refs.dialog.open) this.selectThumbnail(activeIndex, { behavior: 'instant' });
    });
  };

  // Fit-scale navigation shares the card/PDP Scroller; zoomed pointers keep pan/pinch ownership.
  #galleryPosition(media) {
    const list = this.refs.mediaList;
    const horizontal = this.#galleryScroll?.axis === 'x';
    const edge = horizontal ? 'left' : 'top';
    return (horizontal ? list.scrollLeft : list.scrollTop)
      + media.getBoundingClientRect()[edge] - list.getBoundingClientRect()[edge];
  }

  #nearestGalleryIndex() {
    const list = this.refs.mediaList;
    const position = this.#galleryScroll.axis === 'x' ? list.scrollLeft : list.scrollTop;
    let nearest = 0;
    let distance = Infinity;
    this.refs.media.forEach((media, index) => {
      const difference = Math.abs(this.#galleryPosition(media) - position);
      if (difference < distance) { nearest = index; distance = difference; }
    });
    return nearest;
  }

  #handleGalleryGesture = (event) => {
    if (!this.#galleryScroll || !this.refs.dialog.open || this.refs.media.length < 2) return;
    const { phase, delta } = event.detail;
    const ownerIndex = this.refs.media.findIndex(media => media.contains(event.target));
    if (ownerIndex < 0 || (phase !== 'start' && event.target !== this.#activeDetailZoom)) return;
    const list = this.refs.mediaList;
    if (phase === 'start') {
      this.#galleryRevision++;
      this.#galleryScroll.stop();
      this.#galleryScroll.snap = false;
      this.#gallerySettling = false;
      this.#setActiveDetailZoom(ownerIndex);
      const position = this.#galleryScroll.axis === 'x' ? list.scrollLeft : list.scrollTop;
      this.#galleryGesture = { index: this.#activeDetailZoomIndex, position, delta: 0 };
      return;
    }
    const gesture = this.#galleryGesture;
    if (!gesture) return;
    if (phase === 'move') {
      gesture.delta = delta;
      this.#galleryScroll.to(gesture.position + delta, { instant: true });
      return;
    }
    this.#galleryGesture = null;
    if (phase === 'cancel') {
      this.selectThumbnail(gesture.index, { behavior: 'instant' });
      return;
    }
    let index = this.#nearestGalleryIndex();
    const threshold = Math.max(30, Math.min(80, list.clientWidth * .15));
    if (index === gesture.index && Math.abs(gesture.delta) >= threshold) {
      index = getAdjacentMediaIndex(index, this.refs.media.length, Math.sign(gesture.delta));
    }
    this.selectThumbnail(index);
  };

  #stopGallery() {
    this.#galleryRevision++;
    this.#galleryGesture = null;
    this.#gallerySettling = false;
    this.#galleryScroll?.stop();
  }

  #resetDetailZooms() {
    if (!this.#detailZoomEnabled) return;

    for (const wrapper of this.querySelectorAll('product-detail-zoom')) {
      if (typeof wrapper.resetZoom === 'function') wrapper.resetZoom();
    }
    this.#syncZoomControls();
  }

  /** @param {Event} event */
  #handleDetailZoomChange = (event) => {
    const wrapper = event.target instanceof Element ? event.target.closest('product-detail-zoom') : null;
    if (!wrapper || wrapper !== this.#activeDetailZoom) return;

    const media = this.refs.media[this.#activeDetailZoomIndex];
    if (media) this.loadHighResolutionImage(media);
    this.#syncZoomControls();
  };

  #syncZoomControls() {
    const controls = this.refs.zoomControls;
    const zoomOutButton = this.refs.zoomOutButton;
    const zoomResetButton = this.refs.zoomResetButton;
    const zoomInButton = this.refs.zoomInButton;
    const zoomValue = this.refs.zoomValue;
    if (!(controls instanceof HTMLElement)) return;

    const wrapper = this.#activeDetailZoom;
    controls.hidden = !wrapper;

    if (
      !wrapper ||
      !(zoomOutButton instanceof HTMLButtonElement) ||
      !(zoomResetButton instanceof HTMLButtonElement) ||
      !(zoomInButton instanceof HTMLButtonElement) ||
      !(zoomValue instanceof HTMLElement)
    ) {
      return;
    }

    const scale = Number(wrapper.scale ?? 1);
    const minimum = Number(wrapper.minScale ?? 1);
    const maximum = Number(wrapper.maxScale ?? 3);
    zoomOutButton.disabled = scale <= minimum + 0.001;
    zoomResetButton.disabled = scale <= minimum + 0.001;
    zoomInButton.disabled = scale >= maximum - 0.001;
    const percentage = `${Math.round(scale * 100)}%`;
    zoomValue.textContent = percentage;
    const resetLabel = zoomResetButton.dataset.resetLabel ?? 'Reset';
    zoomResetButton.setAttribute('aria-label', `${resetLabel} – ${percentage}`);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
